import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { addToWaitlist } from '../src/lib/supabase';

export default function WaitlistSection() {
  const [isGlitching, setIsGlitching] = useState(false);
  const [discordGlitch, setDiscordGlitch] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error' | 'duplicate'>('idle');
  const [showEmailInput, setShowEmailInput] = useState(false);

  const triggerGlitch = () => {
    setIsGlitching(true);
    setTimeout(() => setIsGlitching(false), 300);
  };

  const triggerDiscordGlitch = () => {
    setDiscordGlitch(true);
    setTimeout(() => setDiscordGlitch(false), 250);
  };

  const handleWaitlistClick = async () => {
    if (!showEmailInput) {
      // First click - show email input
      setShowEmailInput(true);
      triggerGlitch();
      return;
    }

    if (!email.trim()) {
      // No email entered, just trigger glitch
      triggerGlitch();
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      // Invalid email format
      setSubmitStatus('error');
      triggerGlitch();
      
      // Reset error message after 3 seconds
      setTimeout(() => setSubmitStatus('idle'), 3000);
      return;
    }

    // Submit email to waitlist
    setIsSubmitting(true);
    triggerGlitch();

    try {
      await addToWaitlist(email.trim());
      setSubmitStatus('success');
      setEmail('');
      setShowEmailInput(false);
      
      // Reset success message after 3 seconds
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } catch (error) {
      console.error('Error adding to waitlist:', error);
      
      // Check if it's a duplicate email error
      if (error && typeof error === 'object' && 'code' in error &&
          (error.code === '23505' || error.code === 'DUPLICATE_EMAIL')) {
        // PostgreSQL unique constraint violation or our custom duplicate error
        setSubmitStatus('duplicate');
      } else if (error && typeof error === 'object' && 'message' in error &&
                 typeof error.message === 'string' &&
                 (error.message.toLowerCase().includes('duplicate') ||
                  error.message.toLowerCase().includes('already on the waitlist'))) {
        // Generic duplicate error or our custom message
        setSubmitStatus('duplicate');
      } else if (error instanceof Error &&
                 error.message.toLowerCase().includes('already on the waitlist')) {
        // Handle our custom duplicate error message
        setSubmitStatus('duplicate');
      } else {
        setSubmitStatus('error');
      }
      
      // Reset error message after 3 seconds
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiscordClick = () => {
    triggerDiscordGlitch();
    // Discord invite link provided by user
    window.open('https://discord.gg/thQEtJZpzs', '_blank');
  };

  const getWaitlistButtonText = () => {
    if (isSubmitting) return '> JOINING...';
    if (submitStatus === 'success') return '> JOINED!';
    if (submitStatus === 'duplicate') return '> ALREADY WAITLISTED';
    if (submitStatus === 'error') {
      // Check if it's a validation error vs server error
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (email.trim() && !emailRegex.test(email.trim())) {
        return '> INVALID EMAIL FORMAT';
      }
      return '> ERROR - TRY AGAIN';
    }
    if (showEmailInput && email.trim()) return '> SUBMIT EMAIL';
    if (showEmailInput) return '> ENTER EMAIL';
    return '> JOIN THE WAITLIST';
  };

  return (
    <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-40 flex flex-col items-center gap-4">
      {/* Glitch background effect */}
      <div 
        className={`absolute inset-0 pointer-events-none transition-opacity duration-200 ${
          isGlitching || discordGlitch ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background: `
            radial-gradient(circle at 50% 50%, 
              rgba(0, 255, 255, 0.2) 0%, 
              rgba(255, 0, 255, 0.15) 40%, 
              transparent 70%
            )
          `,
          filter: 'blur(20px)',
          transform: 'scale(2)'
        }}
      />

      {/* Email input field - appears when waitlist button is clicked */}
      {showEmailInput && (
        <div className="relative group animate-in fade-in slide-in-from-bottom-2 duration-300">
          <Input
            type="email"
            placeholder="Enter your email..."
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleWaitlistClick();
              }
              if (e.key === 'Escape') {
                setShowEmailInput(false);
                setEmail('');
              }
            }}
            className={`
              w-80 px-4 py-3 bg-black/60 border-2 border-cyan-500/60 
              text-cyan-400 placeholder:text-cyan-400/50
              backdrop-blur-sm transition-all duration-200
              focus:border-cyan-400 focus:ring-cyan-400/30
              ${isGlitching ? 'animate-intense-glitch' : ''}
            `}
            style={{
              fontFamily: "'Courier New', monospace",
              textShadow: '0 0 8px rgba(0, 255, 255, 0.5)',
              boxShadow: `
                0 0 20px rgba(0, 255, 255, 0.3),
                inset 0 0 20px rgba(0, 255, 255, 0.1)
              `
            }}
            autoFocus
          />
        </div>
      )}

      {/* Main waitlist button */}
      <div className="relative group">
        <Button
          onClick={handleWaitlistClick}
          disabled={isSubmitting}
          className={`
            relative px-8 py-3 bg-transparent border-2 
            ${submitStatus === 'success' ? 'border-green-500/60 text-green-400' :
              submitStatus === 'error' ? 'border-red-500/60 text-red-400' :
              submitStatus === 'duplicate' ? 'border-yellow-500/60 text-yellow-400' :
              'border-cyan-500/60 text-cyan-400 hover:text-cyan-300 hover:border-cyan-400'}
            transition-all duration-200 backdrop-blur-sm
            ${isGlitching ? 'animate-intense-glitch' : ''}
            ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}
          `}
          style={{
            fontFamily: "'Courier New', monospace",
            textShadow: isGlitching ? `
              -2px 0 0 rgba(255, 0, 255, 0.8),
              2px 0 0 rgba(0, 255, 255, 0.8),
              0 0 10px rgba(0, 255, 255, 0.6)
            ` : submitStatus === 'success' ? '0 0 10px rgba(0, 255, 0, 0.5)' :
                submitStatus === 'error' ? '0 0 10px rgba(255, 0, 0, 0.5)' :
                submitStatus === 'duplicate' ? '0 0 10px rgba(255, 255, 0, 0.5)' :
                '0 0 10px rgba(0, 255, 255, 0.5)',
            boxShadow: `
              0 0 20px ${submitStatus === 'success' ? 'rgba(0, 255, 0, 0.3)' :
                         submitStatus === 'error' ? 'rgba(255, 0, 0, 0.3)' :
                         submitStatus === 'duplicate' ? 'rgba(255, 255, 0, 0.3)' :
                         'rgba(0, 255, 255, 0.3)'},
              inset 0 0 20px ${submitStatus === 'success' ? 'rgba(0, 255, 0, 0.1)' :
                               submitStatus === 'error' ? 'rgba(255, 0, 0, 0.1)' :
                               submitStatus === 'duplicate' ? 'rgba(255, 255, 0, 0.1)' :
                               'rgba(0, 255, 255, 0.1)'}
            `
          }}
        >
          <span className={isGlitching ? 'animate-rgb-split' : ''}>
            {getWaitlistButtonText()}
          </span>
        </Button>

        {/* Glitch overlay for waitlist button */}
        {isGlitching && (
          <>
            <div 
              className="absolute inset-0 bg-transparent border-2 border-pink-500/80 animate-glitch-layer-1"
              style={{
                clipPath: 'inset(0 0 70% 0)'
              }}
            />
            <div 
              className="absolute inset-0 bg-transparent border-2 border-yellow-400/70 animate-glitch-layer-2"
              style={{
                clipPath: 'inset(60% 0 0 0)'
              }}
            />
          </>
        )}
      </div>

      {/* Discord button */}
      <div className="relative group">
        <Button
          onClick={handleDiscordClick}
          className={`
            relative px-6 py-2 bg-transparent border border-purple-500/60 
            text-purple-400 hover:text-purple-300 hover:border-purple-400
            transition-all duration-200 backdrop-blur-sm flex items-center gap-2
            ${discordGlitch ? 'animate-intense-glitch' : ''}
          `}
          style={{
            fontFamily: "'Courier New', monospace",
            textShadow: discordGlitch ? `
              -1px 0 0 rgba(255, 0, 255, 0.9),
              1px 0 0 rgba(0, 255, 255, 0.9),
              0 0 8px rgba(138, 43, 226, 0.8)
            ` : '0 0 8px rgba(138, 43, 226, 0.5)',
            boxShadow: `
              0 0 15px rgba(138, 43, 226, 0.3),
              inset 0 0 15px rgba(138, 43, 226, 0.1)
            `
          }}
        >
          {/* Discord Icon */}
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="currentColor"
            className={discordGlitch ? 'animate-pulse' : ''}
          >
            <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.0002 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9554 2.4189-2.1568 2.4189Z"/>
          </svg>
          <span className={discordGlitch ? 'animate-rgb-split' : ''}>
            DISCORD
          </span>
        </Button>

        {/* Glitch overlay for Discord button */}
        {discordGlitch && (
          <>
            <div 
              className="absolute inset-0 bg-transparent border border-cyan-500/90 animate-glitch-layer-2"
              style={{
                clipPath: 'inset(0 0 60% 0)'
              }}
            />
            <div 
              className="absolute inset-0 bg-transparent border border-pink-500/80 animate-glitch-layer-1"
              style={{
                clipPath: 'inset(40% 0 0 0)'
              }}
            />
          </>
        )}
      </div>

      {/* Scan lines effect */}
      <div 
        className={`absolute inset-0 pointer-events-none transition-opacity duration-200 ${
          isGlitching || discordGlitch ? 'opacity-60' : 'opacity-20'
        }`}
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(0, 255, 255, 0.1) 1%, transparent 2%)',
          backgroundSize: '3px 100%',
          animation: isGlitching || discordGlitch ? 'scan-lines 0.1s infinite linear' : 'none'
        }}
      />

      {/* Static noise particles */}
      {(isGlitching || discordGlitch) && (
        <div className="absolute inset-0 pointer-events-none">
          {Array.from({ length: 8 }, (_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.8,
                boxShadow: '0 0 4px rgba(0, 255, 255, 0.8)'
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}